import {
  queryOptions,
  useMutation,
  useSuspenseQuery,
} from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { UserPlus, Users } from "lucide-react"
import { useState } from "react"
import { toast } from "sonner"

import { InviteMemberDialog } from "@/components/members/invite-member-dialog"
import { MemberTable } from "@/components/members/member-table"
import { PendingInvitesTable } from "@/components/members/pending-invites-table"
import { RemoveMemberDialog } from "@/components/members/remove-member-dialog"
import { UpdateRoleDialog } from "@/components/members/update-role-dialog"
import { OrganizationGuard } from "@/components/organization-guard"
import { MembersPageSkeleton } from "@/components/skeletons/members-skeleton"
import { Button } from "@/components/ui/button"
import { listInvites, resendInvite } from "@/http/invites"
import type { TMember } from "@/http/organizations"
import { listMembers } from "@/http/organizations"
import { useOrganization } from "@/store/organization"

const membersQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["members", orgSlug],
    queryFn: async () => {
      const response = await listMembers(orgSlug)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

const invitesQueryOptions = (orgSlug: string) =>
  queryOptions({
    queryKey: ["member-invites", orgSlug],
    queryFn: async () => {
      const response = await listInvites(orgSlug)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

/**
 * Members route - displays organization members and allows role management
 */
export const Route = createFileRoute(
  "/_authenticated/$orgSlug/settings/members",
)({
  loader: async ({ context, params }) => {
    await Promise.all([
      context.queryClient.ensureQueryData(membersQueryOptions(params.orgSlug)),
      context.queryClient.ensureQueryData(invitesQueryOptions(params.orgSlug)),
    ])

    return {
      title: "Membros",
      path: `/${params.orgSlug}/settings/members`,
    }
  },
  component: MembersPage,
  pendingComponent: MembersPageSkeleton,
})

function MembersPage() {
  const { currentOrg } = useOrganization()
  const [updateRoleMember, setUpdateRoleMember] = useState<TMember | null>(null)
  const [removeMember, setRemoveMember] = useState<TMember | null>(null)
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [resendingInviteId, setResendingInviteId] = useState<string | null>(
    null,
  )
  const { orgSlug } = Route.useParams()

  // Fetch members data
  const { data, refetch } = useSuspenseQuery(membersQueryOptions(orgSlug))

  const { data: invitesData, refetch: refetchInvites } = useSuspenseQuery(
    invitesQueryOptions(orgSlug),
  )

  const { mutate: resend, isPending: isResending } = useMutation({
    mutationFn: async (inviteId: string) => {
      return resendInvite({
        organizationSlug: currentOrg?.slug ?? "",
        inviteId,
      })
    },
    onMutate: (inviteId) => {
      setResendingInviteId(inviteId)
    },
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error.message || "Erro ao reenviar convite")
        return
      }

      toast.success("Convite reenviado com sucesso!")
      refetchInvites()
    },
    onSettled: () => {
      setResendingInviteId(null)
    },
  })

  const handleUpdateRole = (memberId: string) => {
    const member = data?.members.find((m) => m.id === memberId)
    if (member) {
      setUpdateRoleMember(member)
    }
  }

  const handleRemove = (memberId: string) => {
    const member = data?.members.find((m) => m.id === memberId)
    if (member) {
      setRemoveMember(member)
    }
  }

  const handleSuccess = () => {
    refetch()
    refetchInvites()
  }

  return (
    <OrganizationGuard
      action="manage"
      subject="Member"
      fallback={
        <div className="space-y-2">
          <h1 className="text-2xl font-bold md:text-3xl">Membros</h1>
          <p className="text-muted-foreground">
            Você não tem permissão para visualizar os membros desta organização.
          </p>
        </div>
      }
    >
      <div className="space-y-6">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="flex items-center gap-2">
              <Users className="h-6 w-6" />
              <h1 className="text-2xl font-bold md:text-3xl">Membros</h1>
            </div>
            <p className="text-muted-foreground mt-2">
              Gerencie os membros da organização e seus cargos
            </p>
          </div>
          <Button onClick={() => setInviteDialogOpen(true)}>
            <UserPlus className="mr-2 h-4 w-4" />
            Convidar membro
          </Button>
        </div>

        <MemberTable
          members={data?.members || []}
          onUpdateRole={handleUpdateRole}
          onRemove={handleRemove}
        />

        <div className="space-y-3">
          <h2 className="text-xl font-semibold">Convites pendentes</h2>
          {invitesData?.invites?.length ? (
            <PendingInvitesTable
              invites={invitesData.invites}
              onResend={resend}
              resendingId={isResending ? resendingInviteId : null}
            />
          ) : (
            <p className="text-sm text-muted-foreground">
              Nenhum convite pendente encontrado.
            </p>
          )}
        </div>

        {updateRoleMember && (
          <UpdateRoleDialog
            open={!!updateRoleMember}
            onOpenChange={(open) => !open && setUpdateRoleMember(null)}
            memberId={updateRoleMember.id}
            memberName={updateRoleMember.user.name}
            currentRole={updateRoleMember.role}
            organizationSlug={currentOrg?.slug ?? ""}
            onSuccess={handleSuccess}
          />
        )}

        {removeMember && (
          <RemoveMemberDialog
            open={!!removeMember}
            onOpenChange={(open) => !open && setRemoveMember(null)}
            memberId={removeMember.id}
            memberName={removeMember.user.name}
            organizationSlug={currentOrg?.slug ?? ""}
            onSuccess={handleSuccess}
          />
        )}

        <InviteMemberDialog
          open={inviteDialogOpen}
          onOpenChange={setInviteDialogOpen}
          organizationSlug={currentOrg?.slug ?? ""}
          onSuccess={handleSuccess}
        />
      </div>
    </OrganizationGuard>
  )
}
